import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConfirmDeleteDialog,
  plural,
  SelectionAction,
  SelectionBar,
  TopbarActions,
  TopbarMoreMenu,
  TopbarPill,
} from '@/components/ui/admin/gallery';
import {
  AttachVideoDialog,
  type CurationToast,
  CurationToastView,
  HOME_LIMIT,
  HomeBadge,
  HomeLimitNotice,
  selectionLabel,
  type Testimonial,
  TestimonialActions,
  TestimonialCard,
  type TestimonialFilter,
  TestimonialFilters,
  TestimonialsEmpty,
  TestimonialsIntro,
  TestimonialsMasonry,
  TestimonialsSkeleton,
  type TestimonialVideo,
  VideoPlayer,
} from '@/components/ui/admin/testimonials';
import { Content } from '@/components/ui/content';
import { useDebounce } from '@/hooks/use-debounce';
import { meta } from '@/lib/meta';
import {
  deleteTestimonials,
  flattenTestimonials,
  patchTestimonials,
  uploadTestimonialVideo,
  useRefreshSummary,
  useSetTestimonials,
  useTestimonialPages,
  useTestimonialSummary,
} from '@/services/testimonials';

/**
 * Testemunhos (módulo 13, prints 18–22 do `aliste-se-admin-kit`). Tela de curadoria: o texto chega
 * pelo site (`/testemunhos`); aqui só "Mostrar no início" (publica no site), vídeo e excluir — tudo otimista.
 * A lista vem em páginas (`useInfiniteQuery`) e carrega mais ao rolar; busca, acampamento e filtro rodam na
 * API, e os contadores vêm de `GET /testimonials/summary`.
 */
export const Route = createFileRoute('/_app/testimonials')({
  head: () => meta({ label: 'Testemunhos' }),
  component: RouteComponent,
});

const SAVE_ERROR: CurationToast = {
  kind: 'error',
  title: 'Não foi possível salvar',
  body: 'A alteração foi desfeita. Verifique a conexão e tente de novo.',
};

function RouteComponent() {
  const [filter, setFilter] = useState<TestimonialFilter>('all');
  const [camp, setCamp] = useState('all');
  const [query, setQuery] = useState('');
  const search = useDebounce(query.trim(), 300);
  const campParam = camp === 'all' ? '' : camp;

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useTestimonialPages({ search, camp: campParam, filter });
  const { data: summary, isLoading: loadingSummary } = useTestimonialSummary(campParam);
  const setItems = useSetTestimonials();
  const refreshSummary = useRefreshSummary();
  const items = flattenTestimonials(data);
  const matched = data?.pages[0]?.pagination.total ?? 0;
  const moreRef = useRef<HTMLDivElement>(null);

  // Carrega a próxima página quando o fim da grade entra na tela.
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '600px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [attachFor, setAttachFor] = useState<Testimonial | null>(null);
  const [savingVideo, setSavingVideo] = useState(false);
  const [playing, setPlaying] = useState<Testimonial | null>(null);
  const [toDelete, setToDelete] = useState<string[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<CurationToast>(null);
  const closeToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (isError)
      setToast({
        kind: 'error',
        title: 'Não foi possível carregar',
        body: 'Recarregue a página em instantes.',
      });
  }, [isError]);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected([]);
  }, []);
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Esc: dialog e player tratam o próprio Esc — aqui só sobra a seleção.
  useEffect(() => {
    if (!selectMode) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !attachFor && !toDelete && !playing) exitSelect();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [selectMode, attachFor, toDelete, playing, exitSelect]);

  /** Otimista: aplica na hora, desfaz + toast se a API falhar. */
  const patch = async (
    ids: string[],
    data: Pick<Partial<Testimonial>, 'showOnHome' | 'video'>,
    ok?: CurationToast,
  ) => {
    const undo = setItems((xs) =>
      xs.map((t) => (ids.includes(t.id) ? { ...t, ...data } : t)),
    );
    try {
      await patchTestimonials(ids, data);
      if (ok) setToast(ok);
    } catch {
      undo();
      setToast(SAVE_ERROR);
    } finally {
      void refreshSummary();
    }
  };

  const attach = async (video: TestimonialVideo) => {
    if (!attachFor) return;
    setSavingVideo(true);
    await patch(
      [attachFor.id],
      { video },
      {
        kind: 'ok',
        title: 'Vídeo anexado',
        body: 'O testemunho já aparece com o vídeo no site.',
      },
    );
    setSavingVideo(false);
    setAttachFor(null);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteTestimonials(toDelete);
      setItems((xs) => xs.filter((t) => !toDelete.includes(t.id)));
      setToast({
        kind: 'ok',
        title:
          toDelete.length === 1
            ? 'Testemunho excluído'
            : plural(toDelete.length, 'testemunho excluído', 'testemunhos excluídos'),
        body: 'Já não aparece no site.',
      });
      exitSelect();
    } catch {
      setToast({
        kind: 'error',
        title: 'Não foi possível excluir',
        body: 'O testemunho continua no site. Tente de novo em instantes.',
      });
    } finally {
      setDeleting(false);
      setToDelete(null);
      void refreshSummary();
    }
  };

  // A API já devolve só o que combina com busca/acampamento/filtro.
  const visible = items;
  const counts = summary?.counts ?? { all: 0, home: 0, video: 0, novideo: 0 };
  const homeCount = summary?.totals.home ?? 0;
  const clearFilters = () => {
    setFilter('all');
    setCamp('all');
    setQuery('');
  };
  const reviewHome = () => {
    setCamp('all');
    setQuery('');
    setFilter('home');
  };
  const hint = isLoading
    ? 'Carregando…'
    : selectMode
      ? 'Toque nos cards para selecionar'
      : query.trim()
        ? `${plural(matched, 'resultado', 'resultados')} para a busca`
        : `Exibindo ${matched} • mais recentes primeiro`;
  const n = toDelete?.length ?? 0;
  const selToggle = () => (selectMode ? exitSelect() : setSelectMode(true));

  return (
    <Content
      breadcrumbs={{
        title: 'Testemunhos',
        items: [{ label: 'Conteúdo' }, { label: 'Testemunhos', active: true }],
        // Curadoria: sem botão areia de criar — só "Selecionar" (no celular, dentro do "⋯").
        action: (
          <TopbarActions>
            <span className={selectMode ? 'contents' : 'contents max-sm:hidden'}>
              <TopbarPill
                icon={selectMode ? 'x' : 'square-check'}
                active={selectMode}
                onClick={selToggle}
              >
                {selectMode ? 'Cancelar seleção' : 'Selecionar'}
              </TopbarPill>
            </span>
            {!selectMode && (
              <TopbarMoreMenu
                icon="ellipsis-vertical"
                className="size-11 rounded-xl text-foreground sm:hidden"
                items={[
                  { icon: 'square-check', label: 'Selecionar', onSelect: selToggle },
                  { icon: 'house', label: 'Ver os do início', onSelect: reviewHome },
                ]}
              />
            )}
          </TopbarActions>
        ),
      }}
    >
      <div className="flex w-full flex-col gap-[22px] pb-[120px]">
        <TestimonialsIntro
          total={summary?.totals.all ?? 0}
          home={homeCount}
          withVideo={summary?.totals.video ?? 0}
          loading={loadingSummary}
        />
        {!loadingSummary && <HomeLimitNotice count={homeCount} onReview={reviewHome} />}
        <TestimonialFilters
          query={query}
          onQuery={setQuery}
          camp={camp}
          camps={summary?.camps ?? []}
          onCamp={setCamp}
          filter={filter}
          onFilter={setFilter}
          counts={counts}
          hint={hint}
        />

        {isLoading ? (
          <TestimonialsSkeleton />
        ) : !summary?.totals.all ? (
          <TestimonialsEmpty variant="all" />
        ) : !visible.length ? (
          <TestimonialsEmpty
            variant="filter"
            query={query.trim()}
            onClear={clearFilters}
          />
        ) : (
          <TestimonialsMasonry
            items={visible}
            render={(t) => (
              <TestimonialCard
                testimonial={t}
                onPlay={() => setPlaying(t)}
                onRemoveVideo={() =>
                  patch(
                    [t.id],
                    { video: null },
                    {
                      kind: 'ok',
                      title: 'Vídeo removido',
                      body: 'O testemunho continua no site, só com o texto.',
                    },
                  )
                }
                badges={t.showOnHome ? <HomeBadge /> : null}
                selectable={selectMode}
                selected={selected.includes(t.id)}
                onSelect={() => toggle(t.id)}
                actions={
                  selectMode ? null : (
                    <TestimonialActions
                      showOnHome={t.showOnHome}
                      hasVideo={!!t.video}
                      onToggleHome={(v) => patch([t.id], { showOnHome: v })}
                      onAttach={() => setAttachFor(t)}
                      onDelete={() => setToDelete([t.id])}
                    />
                  )
                }
              />
            )}
          />
        )}
        {hasNextPage && (
          <div ref={moreRef} className="flex justify-center py-4">
            <span className="col-label text-[10.5px] text-faint">
              {isFetchingNextPage ? 'Carregando mais…' : ''}
            </span>
          </div>
        )}
      </div>

      {selectMode && selected.length > 0 && (
        <SelectionBar
          label={selectionLabel(selected.length)}
          selectAllLabel="Marcar todos"
          onSelectAll={
            selected.length < visible.length
              ? () => setSelected(visible.map((t) => t.id))
              : undefined
          }
          onDelete={() => setToDelete(selected)}
          onCancel={exitSelect}
        >
          <SelectionAction
            icon="house"
            onClick={() => {
              const ids = selected;
              exitSelect();
              patch(
                ids,
                { showOnHome: true },
                {
                  kind: 'ok',
                  title: `${plural(ids.length, 'testemunho', 'testemunhos')} no início`,
                  body: `Já aparecem em /testemunhos; a página inicial mostra os ${HOME_LIMIT} mais recentes.`,
                },
              );
            }}
          >
            Mostrar no início
          </SelectionAction>
          <SelectionAction
            onClick={() => {
              const ids = selected;
              exitSelect();
              patch(
                ids,
                { showOnHome: false },
                {
                  kind: 'ok',
                  title: `${plural(ids.length, 'testemunho saiu', 'testemunhos saíram')} do início`,
                  body: 'Já não aparecem no site.',
                },
              );
            }}
          >
            Tirar do início
          </SelectionAction>
        </SelectionBar>
      )}

      <VideoPlayer
        testimonial={playing}
        onClose={() => setPlaying(null)}
        paused={!!toDelete || !!attachFor}
      />
      <AttachVideoDialog
        open={!!attachFor}
        testimonial={attachFor}
        upload={uploadTestimonialVideo}
        loading={savingVideo}
        onClose={() => setAttachFor(null)}
        onSubmit={attach}
      />
      <ConfirmDeleteDialog
        open={!!toDelete}
        loading={deleting}
        title={n === 1 ? 'Excluir testemunho?' : `Excluir ${n} testemunhos?`}
        body={
          n === 1
            ? 'Ele sai do site e não pode ser desfeito.'
            : 'Eles saem do site e não pode ser desfeito.'
        }
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
      <CurationToastView toast={toast} onClose={closeToast} />
    </Content>
  );
}
